import {
  filter,
  find,
  flow,
  forEach,
  map,
  matches,
  merge,
  omit,
  sortBy,
  uniqBy,
} from 'lodash';

import {
  ILegacyColor,
  ILegacyColorId,
  getUniqueColors,
} from '@classes/legacy-color';

/**
 * Model for sku as used inside {@link ProductModel}
 */
export class ProductSkuModel implements ILegacyColor {
  barcode: string;
  // TODO consider moving to product level
  brand: string;
  colorCode?: string;
  colorCodeSupplier?: string;
  colorDescription?: string;
  colorSupplier?: string;
  colorFamily: string;
  mainColorCode?: string;
  mainColorDescription?: string;
  size?: string;
  sizeIndex?: number;
  skuActive?: boolean;
  // TODO double check if it's still used
  subcolor?: string;
  subColorCode?: string;
  subColorDescription: string;
  warehouseLocation?: string;
  remarks?: string;
  // TODO define separate type for modified product
  qty?: number;
}

export type PartialProductModel = Partial<Omit<ProductObject, 'skus'>> & {
  skus?: Partial<ProductSkuModel>[];
};

/**
 * Class for Product.
 */
// TODO rename to ProductModel
export class ProductObject {
  active: boolean;
  articleCode: string;
  articleCodeSupplier: string;
  articleDescription: string;
  articleGroup: string;
  brand: string;
  buyPrice: number;
  care: {
    temperature: number;
    bleach: number;
    iron: number;
    chemical: number;
    dryer: number;
  };
  collection: string;
  directDelivery: 'on' | 'off' | 'unavailable';
  groups: { name: string; value: string }[];
  localizedBasePrice: number;
  localizedPrice: number;
  localizedSuggestedRetailPrice: number;
  originalPrice: number;
  price: number;
  season: string;
  sizeRange: string;
  sizes: string[];
  skus: ProductSkuModel[];
  suggestedRetailPrice: number;
  supplierName: string;
  tagType: string;
  valuePrice: number;
  vat: number;
  year: string;
  // TODO move to SalesOrderProductModel
  qty?: number;

  constructor(originalValue?: PartialProductModel) {
    merge(this, originalValue);
  }

  private setRemarks(remarksObject: ILegacyColorId & { remarks: string }) {
    let matchingSkus = filter(
      this.skus,
      matches(omit(remarksObject, 'remarks')),
    );

    forEach(matchingSkus, (sku) => {
      sku.remarks = remarksObject.remarks;
    });
  }

  setRemarksFromMap(remarksArray: (ILegacyColorId & { remarks: string })[]) {
    forEach(remarksArray, (value) => {
      this.setRemarks(value);
    });
  }

  // TODO unit
  setSkusFromDataArray(dataArray: { id: string; value: number }[]) {
    forEach(this.skus, (sku) => {
      let match = find(dataArray, { id: sku.barcode });

      sku.qty = match.value;
    });
  }

  /**
   * Returns an array of color objects based on the available colors in the product
   * ```
   * [
   *  {
   *    colorDescription: 'blue',
   *    colorCode: '001'
   *  }
   * ]
   * ```
   * @returns {array<object>} array of color objects
   */
  getColors(): ILegacyColorId[] {
    return getUniqueColors(this.skus);
  }

  /**
   * Returns an array of color objects based on the available colors in the product
   * ```
   * [ 's', 'm', 'l' ]
   * ```
   * @returns {strigt[]} array of sizes
   */
  getSizes(): string[] {
    return flow([
      (x) => uniqBy(x, 'size'),
      (x) => sortBy(x, 'sizeIndex'),
      (x) => map(x, (x) => x.size),
    ])(this.skus);
  }
}

export const ProductObjectProvider = {
  provide: 'ProductObject',
  useValue: ProductObject,
};
